import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { formatDate, formatMoney, formatNumber } from '../shared/formatting';
import { BrandSpinner } from '../shared/BrandSpinner';
import { Breadcrumb } from '../shared/Breadcrumb';
import { LagerAssortmentsPanel } from './lagerAssortmentsPanel';

type Locale = 'hr' | 'en';

type WeightClass = { id: number; code: string; name_hr: string; fine_weight_g: number };

type Branch = { code: string; name: string };

type Cell = {
    key: string;
    plan_id: number | null;
    warehouse_code: string;
    warehouse_name: string;
    weight_class_id: number;
    weight_class_code: string;
    fine_weight_g: number;
    target_qty: number;
    plan_fine_g: number;
    plan_value_eur: number;
    stock_qty: number;
    stock_fine_g: number;
    stock_value_eur: number;
    gap_qty: number;
    gap_fine_g: number;
    gap_value_eur: number;
    status: 'draft' | 'proposed' | 'approved' | 'rejected';
    note: string | null;
    approved_at: string | null;
};

type Total = {
    plan_qty: number;
    plan_fine_g: number;
    plan_value_eur: number;
    draft_qty: number;
    stock_qty: number;
    stock_fine_g: number;
    stock_value_eur: number;
    gap_qty: number;
    gap_fine_g: number;
    gap_value_eur: number;
};

type PromoAllocation = {
    warehouse_code: string;
    plan_value_eur: number;
    share: number;
    promo_value_eur: number;
    promo_fine_g: number;
};

type Promo = {
    policy: {
        id: number | null;
        max_value_eur: number;
        allocation_mode: string;
        status: string;
        valid_from: string;
        approved_at: string | null;
        note: string | null;
    };
    base_plan_value_eur: number;
    allocation: PromoAllocation[];
};

type Matrix = {
    metal_type: string;
    valid_from: string;
    spot: { eur_per_gram: number; eur_per_troy_oz: number; spot_date: string | null };
    stock_batch_id: number | null;
    stock_as_of: string | null;
    branches: Branch[];
    replenish?: { code: string; name: string; stock_qty: number; stock_fine_g: number; stock_value_eur: number }[];
    weight_classes: WeightClass[];
    cells: Cell[];
    totals: {
        by_branch: Record<string, Total>;
        by_weight_class: Record<string, Total>;
        grand: Total;
    };
    unclassified_stock: { warehouse_code: string; qty: number; fine_g: number }[];
    promo: Promo;
    can_edit: boolean;
    can_approve: boolean;
    saved?: number;
    deleted?: number;
    affected?: number;
};

type GapResponse = {
    metal_type: string;
    valid_from: string;
    rows: {
        warehouse_code: string;
        warehouse_name: string;
        weight_class_id: number;
        weight_class_code: string;
        target_qty: number;
        stock_qty: number;
        gap_qty: number;
        gap_fine_g: number;
        gap_value_eur: number;
    }[];
    totals: { gap_qty: number; gap_fine_g: number; gap_value_eur: number };
};

type ViewMode = 'assortments' | 'plan' | 'stock' | 'gap';

const T = {
    hr: {
        title: 'Planiranje lagera',
        lead: 'Ciljana zaliha po poslovnici i apoenu, u komadima. EUR se ne unosi nego racuna po spotu.',
        warehouse: 'Skladiste',
        metal: 'Metal',
        validFrom: 'Vrijedi od',
        view: 'Prikaz',
        plan: 'Plan (kom)',
        stock: 'Stanje danas (kom)',
        gap: 'Razlika plan minus stanje',
        total: 'Ukupno',
        save: 'Spremi nacrt',
        submit: 'Posalji na odobrenje',
        approve: 'Odobri predlozeno',
        reject: 'Odbij predlozeno',
        promoTitle: 'Akcijsko skladiste (L2)',
        promoLead: 'Gornja granica vrijednosti akcijskog skladista. Razdioba po poslovnicama je omjerna prema udjelu u odobrenom planu i informativna je, jer je akcijsko fizicki jedno skladiste.',
        promoMax: 'Najvise EUR',
        promoSave: 'Spremi',
        promoApprove: 'Spremi i odobri',
        share: 'Udio',
        gapTitle: 'Razlika za Plan & Generate',
        gapLead: 'Samo odobreni redci u kojima roba fali. Ovo je ulaz za nabavu i preskladistenje.',
        spot: 'Spot',
        stockAsOf: 'Stanje na dan',
        noBatch: 'Nema Faros batcha, stanje je nula.',
        unclassified: 'Zaliha bez apoena',
        unclassifiedLead: 'Artikli kojima se nije dao odrediti tezinski razred. Ne ulaze ni u jedan redak matrice; rijesiti kroz 40.1.',
        saved: 'Spremljeno',
        nothing: 'Nema promjena za spremanje.',
        legend: 'Status celije: nacrt bez oznake, P predlozeno, O odobreno, X odbijeno.',
        noEdit: 'Nemate ovlast za unos, prikaz je samo za citanje.',
        loading: 'Ucitavam',
        pieces: 'kom',
    },
    en: {
        title: 'Lager planning',
        lead: 'Target stock per branch and denomination, in pieces. EUR is computed at spot, not entered.',
        warehouse: 'Warehouse',
        metal: 'Metal',
        validFrom: 'Valid from',
        view: 'View',
        plan: 'Plan (pcs)',
        stock: 'Stock today (pcs)',
        gap: 'Plan minus stock',
        total: 'Total',
        save: 'Save draft',
        submit: 'Submit for approval',
        approve: 'Approve proposed',
        reject: 'Reject proposed',
        promoTitle: 'Promo warehouse (L2)',
        promoLead: 'Value cap for the promo warehouse. Split per branch is proportional to the approved plan share and informative only, the promo warehouse being physically one location.',
        promoMax: 'Max EUR',
        promoSave: 'Save',
        promoApprove: 'Save and approve',
        share: 'Share',
        gapTitle: 'Gap for Plan & Generate',
        gapLead: 'Approved rows with a shortage only. This feeds purchasing and restocking.',
        spot: 'Spot',
        stockAsOf: 'Stock as of',
        noBatch: 'No Faros batch, stock reads as zero.',
        unclassified: 'Stock without denomination',
        unclassifiedLead: 'Items with no resolvable weight class. They enter no matrix row; fix in 40.1.',
        saved: 'Saved',
        nothing: 'Nothing to save.',
        legend: 'Cell status: draft unmarked, P proposed, O approved, X rejected.',
        noEdit: 'No entry permission, read only.',
        loading: 'Loading',
        pieces: 'pcs',
    },
};

const STATUS_MARK: Record<Cell['status'], string> = {
    draft: '',
    proposed: 'P',
    approved: 'O',
    rejected: 'X',
};

function firstOfMonth(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function LagerPlanModule({
    token,
    locale,
    onBack,
}: {
    token: string;
    locale: Locale;
    onBack?: () => void;
}) {
    const t = T[locale];
    const [metal, setMetal] = useState('gold');
    const [validFrom, setValidFrom] = useState(firstOfMonth());
    const [view, setView] = useState<ViewMode>('assortments');
    const [matrix, setMatrix] = useState<Matrix | null>(null);
    const [gap, setGap] = useState<GapResponse | null>(null);
    const [edits, setEdits] = useState<Record<string, string>>({});
    const [promoMax, setPromoMax] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const query = `metal_type=${encodeURIComponent(metal)}&valid_from=${encodeURIComponent(validFrom)}`;

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [m, g] = await Promise.all([
                api<Matrix>(`/lager-plan/matrix?${query}`, token),
                api<GapResponse>(`/lager-plan/gap?${query}`, token),
            ]);
            setMatrix(m);
            setGap(g);
            setEdits({});
            setPromoMax(String(m.promo.policy.max_value_eur ?? 0));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setLoading(false);
        }
    }, [query, token]);

    useEffect(() => {
        void load();
    }, [load]);

    const cellByKey = useMemo(() => {
        const map: Record<string, Cell> = {};
        (matrix?.cells ?? []).forEach((c) => {
            map[c.key] = c;
        });
        return map;
    }, [matrix]);

    const dirtyCells = useMemo(() => {
        const out: { warehouse_code: string; weight_class_id: number; target_qty: number }[] = [];
        Object.entries(edits).forEach(([key, raw]) => {
            const cell = cellByKey[key];
            if (!cell) return;
            const parsed = Number(raw.replace(',', '.'));
            const qty = Number.isFinite(parsed) ? parsed : 0;
            if (qty === cell.target_qty) return;
            out.push({ warehouse_code: cell.warehouse_code, weight_class_id: cell.weight_class_id, target_qty: qty });
        });
        return out;
    }, [edits, cellByKey]);

    const save = async (submit: boolean) => {
        if (dirtyCells.length === 0) {
            setNotice(t.nothing);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const next = await mutate<Matrix>('/lager-plan/matrix', token, 'POST', {
                metal_type: metal,
                valid_from: validFrom,
                submit,
                cells: dirtyCells,
            });
            setMatrix(next);
            setEdits({});
            setNotice(`${t.saved}: ${next.saved ?? 0}`);
            setGap(await api<GapResponse>(`/lager-plan/gap?${query}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const decide = async (decision: 'approve' | 'reject') => {
        setBusy(true);
        setError(null);
        try {
            const next = await mutate<Matrix>('/lager-plan/approve', token, 'POST', {
                metal_type: metal,
                valid_from: validFrom,
                decision,
            });
            setMatrix(next);
            setNotice(`${t.saved}: ${next.affected ?? 0}`);
            setGap(await api<GapResponse>(`/lager-plan/gap?${query}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    const savePromo = async (approve: boolean) => {
        setBusy(true);
        setError(null);
        try {
            const parsed = Number(promoMax.replace(',', '.'));
            const res = await mutate<{ promo: Promo }>('/lager-plan/promo-policy', token, 'PUT', {
                metal_type: metal,
                valid_from: validFrom,
                max_value_eur: Number.isFinite(parsed) ? parsed : 0,
                approve,
            });
            setMatrix((prev) => (prev ? { ...prev, promo: res.promo } : prev));
            setNotice(t.saved);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="card admin-block lager-plan-module">
                <BrandSpinner label={t.loading} />
            </div>
        );
    }

    if (!matrix) {
        return (
            <div className="card admin-block lager-plan-module">
                <p className="error">{error ?? 'N/A'}</p>
            </div>
        );
    }

    const perGram = matrix.spot.eur_per_gram;
    const canEdit = matrix.can_edit;

    const cellDisplay = (cell: Cell | undefined): string => {
        if (!cell) return '';
        if (view === 'stock') return formatNumber(cell.stock_qty, locale);
        if (view === 'gap') return formatNumber(cell.gap_qty, locale);
        return formatNumber(cell.target_qty, locale);
    };

    return (
        <div className="card admin-block lager-plan-module">
            <Breadcrumb
                items={[
                    { label: locale === 'hr' ? 'Skladiste' : 'Warehouse', onClick: onBack },
                    { label: `40.8 ${t.title}` },
                ]}
            />
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🧱</div>
                <div className="bi-module-hero-text">
                    <h1>40.8 · {t.title}</h1>
                    <p className="muted">{t.lead}</p>
                </div>
            </div>

            <div className="filters flat">
                <label>
                    {t.metal}
                    <select value={metal} onChange={(e) => setMetal(e.target.value)} disabled={busy}>
                        <option value="gold">{locale === 'hr' ? 'Zlato' : 'Gold'}</option>
                        <option value="silver">{locale === 'hr' ? 'Srebro' : 'Silver'}</option>
                    </select>
                </label>
                <label>
                    {t.validFrom}
                    <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} disabled={busy} />
                </label>
                <label>
                    {t.view}
                    <select value={view} onChange={(e) => setView(e.target.value as ViewMode)}>
                        <option value="assortments">{locale === 'hr' ? 'Asortimani' : 'Assortments'}</option>
                        <option value="plan">{t.plan}</option>
                        <option value="stock">{t.stock}</option>
                        <option value="gap">{t.gap}</option>
                    </select>
                </label>
                <span className="muted">
                    {t.spot}: {formatMoney(perGram, locale)} / g
                    {matrix.spot.spot_date ? ` (${formatDate(matrix.spot.spot_date, locale)})` : ''}
                </span>
                <span className="muted">
                    {matrix.stock_as_of ? `${t.stockAsOf}: ${formatDate(matrix.stock_as_of, locale)}` : t.noBatch}
                </span>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {notice ? <p className="muted">{notice}</p> : null}
            {!canEdit ? <p className="muted">{t.noEdit}</p> : null}

            {view === 'assortments' ? <LagerAssortmentsPanel locale={locale} token={token} metal={metal} /> : (<>

            <div className="table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{locale === 'hr' ? 'Apoen' : 'Denomination'}</th>
                            {matrix.branches.map((b) => (
                                <th key={b.code} title={b.name}>
                                    {b.code}
                                </th>
                            ))}
                            <th>{t.total}</th>
                            <th>EUR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matrix.weight_classes.map((wc) => {
                            const rowTotal = matrix.totals.by_weight_class[String(wc.id)];
                            return (
                                <tr key={wc.id}>
                                    <th scope="row">{wc.name_hr}</th>
                                    {matrix.branches.map((b) => {
                                        const key = `${b.code}|${wc.id}`;
                                        const cell = cellByKey[key];
                                        const editable = canEdit && view === 'plan';
                                        return (
                                            <td key={key} className={cell && cell.gap_qty < 0 && view === 'gap' ? 'negative' : undefined}>
                                                {editable ? (
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step="1"
                                                        className="input"
                                                        value={edits[key] ?? String(cell?.target_qty ?? 0)}
                                                        onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                                                        title={
                                                            cell
                                                                ? `${formatMoney(cell.plan_value_eur, locale)} · ${formatNumber(cell.plan_fine_g, locale)} g`
                                                                : undefined
                                                        }
                                                    />
                                                ) : (
                                                    cellDisplay(cell)
                                                )}
                                                {cell && STATUS_MARK[cell.status] ? (
                                                    <sup className="muted"> {STATUS_MARK[cell.status]}</sup>
                                                ) : null}
                                            </td>
                                        );
                                    })}
                                    <td>
                                        {rowTotal
                                            ? formatNumber(
                                                  view === 'stock' ? rowTotal.stock_qty : view === 'gap' ? rowTotal.gap_qty : rowTotal.plan_qty,
                                                  locale,
                                              )
                                            : '0'}
                                    </td>
                                    <td>
                                        {rowTotal
                                            ? formatMoney(
                                                  view === 'stock'
                                                      ? rowTotal.stock_value_eur
                                                      : view === 'gap'
                                                        ? rowTotal.gap_value_eur
                                                        : rowTotal.plan_value_eur,
                                                  locale,
                                              )
                                            : formatMoney(0, locale)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th scope="row">{t.total}</th>
                            {matrix.branches.map((b) => {
                                const colTotal = matrix.totals.by_branch[b.code];
                                return (
                                    <th key={b.code}>
                                        {colTotal
                                            ? formatNumber(
                                                  view === 'stock' ? colTotal.stock_qty : view === 'gap' ? colTotal.gap_qty : colTotal.plan_qty,
                                                  locale,
                                              )
                                            : '0'}
                                    </th>
                                );
                            })}
                            <th>
                                {formatNumber(
                                    view === 'stock'
                                        ? matrix.totals.grand.stock_qty
                                        : view === 'gap'
                                          ? matrix.totals.grand.gap_qty
                                          : matrix.totals.grand.plan_qty,
                                    locale,
                                )}
                            </th>
                            <th>
                                {formatMoney(
                                    view === 'stock'
                                        ? matrix.totals.grand.stock_value_eur
                                        : view === 'gap'
                                          ? matrix.totals.grand.gap_value_eur
                                          : matrix.totals.grand.plan_value_eur,
                                    locale,
                                )}
                            </th>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <p className="muted">{t.legend}</p>
            {(matrix.replenish ?? []).map((r) => (
                <p key={r.code} className="muted">
                    {locale === 'hr'
                        ? `Nadopuna lagera ${r.code} ${r.name}: ${formatNumber(r.stock_qty, locale)} kom, ${formatNumber(r.stock_fine_g, locale)} g, ${formatMoney(r.stock_value_eur, locale)} po spotu. Ulazi u L1 stanje, nije stupac plana poslovnica.`
                        : `Lager replenishment ${r.code} ${r.name}: ${formatNumber(r.stock_qty, locale)} pcs, ${formatNumber(r.stock_fine_g, locale)} g, ${formatMoney(r.stock_value_eur, locale)} at spot. Counts in L1 stock, not a branch plan column.`}
                </p>
            ))}

            {canEdit ? (
                <div className="form-actions">
                    <button type="button" onClick={() => void save(false)} disabled={busy}>
                        {t.save}
                    </button>
                    <button type="button" onClick={() => void save(true)} disabled={busy}>
                        {t.submit}
                    </button>
                </div>
            ) : null}

            {matrix.can_approve ? (
                <div className="form-actions">
                    <button type="button" onClick={() => void decide('approve')} disabled={busy}>
                        {t.approve}
                    </button>
                    <button type="button" onClick={() => void decide('reject')} disabled={busy}>
                        {t.reject}
                    </button>
                </div>
            ) : null}

            {matrix.unclassified_stock.length > 0 ? (
                <section className="card">
                    <h3>{t.unclassified}</h3>
                    <p className="muted">{t.unclassifiedLead}</p>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>{t.warehouse}</th>
                                <th>{t.pieces}</th>
                                <th>fine g</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matrix.unclassified_stock.map((u) => (
                                <tr key={u.warehouse_code}>
                                    <td>{u.warehouse_code}</td>
                                    <td>{formatNumber(u.qty, locale)}</td>
                                    <td>{formatNumber(u.fine_g, locale)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </section>
            ) : null}

            <section className="card">
                <h3>{t.promoTitle}</h3>
                <p className="muted">{t.promoLead}</p>
                <div className="filters flat">
                    <label>
                        {t.promoMax}
                        <input
                            type="number"
                            min={0}
                            step="100"
                            value={promoMax}
                            onChange={(e) => setPromoMax(e.target.value)}
                            disabled={!canEdit || busy}
                        />
                    </label>
                    {canEdit ? (
                        <button type="button" onClick={() => void savePromo(false)} disabled={busy}>
                            {t.promoSave}
                        </button>
                    ) : null}
                    {matrix.can_approve ? (
                        <button type="button" onClick={() => void savePromo(true)} disabled={busy}>
                            {t.promoApprove}
                        </button>
                    ) : null}
                    <span className="muted">{matrix.promo.policy.status}</span>
                </div>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t.warehouse}</th>
                            <th>{t.plan} EUR</th>
                            <th>{t.share}</th>
                            <th>{t.promoTitle} EUR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {matrix.promo.allocation.map((a) => (
                            <tr key={a.warehouse_code}>
                                <td>{a.warehouse_code}</td>
                                <td>{formatMoney(a.plan_value_eur, locale)}</td>
                                <td>{formatNumber(Math.round(a.share * 1000) / 10, locale)} %</td>
                                <td>{formatMoney(a.promo_value_eur, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="card">
                <h3>{t.gapTitle}</h3>
                <p className="muted">{t.gapLead}</p>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>{t.warehouse}</th>
                            <th>{locale === 'hr' ? 'Apoen' : 'Denomination'}</th>
                            <th>{t.plan}</th>
                            <th>{t.stock}</th>
                            <th>{t.gap}</th>
                            <th>fine g</th>
                            <th>EUR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(gap?.rows ?? []).map((r) => (
                            <tr key={`${r.warehouse_code}-${r.weight_class_id}`}>
                                <td>{r.warehouse_code}</td>
                                <td>{r.weight_class_code}</td>
                                <td>{formatNumber(r.target_qty, locale)}</td>
                                <td>{formatNumber(r.stock_qty, locale)}</td>
                                <td>{formatNumber(r.gap_qty, locale)}</td>
                                <td>{formatNumber(r.gap_fine_g, locale)}</td>
                                <td>{formatMoney(r.gap_value_eur, locale)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <th colSpan={4}>{t.total}</th>
                            <th>{formatNumber(gap?.totals.gap_qty ?? 0, locale)}</th>
                            <th>{formatNumber(gap?.totals.gap_fine_g ?? 0, locale)}</th>
                            <th>{formatMoney(gap?.totals.gap_value_eur ?? 0, locale)}</th>
                        </tr>
                    </tfoot>
                </table>
            </section>
            </>)}
        </div>
    );
}
